import React, { useState } from 'react';
import { toast } from 'sonner';

const MONTHLY_PRICE = process.env.REACT_APP_STRIPE_PRICE_MONTHLY || '';
const YEARLY_PRICE  = process.env.REACT_APP_STRIPE_PRICE_YEARLY  || '';

/**
 * PaywallCard — shown when a feature requires Pro.
 *
 * Props:
 *   feature       string   — name of the gated feature (e.g. "Create Meditation")
 *   reason        string   — why they need Pro (optional)
 *   onUpgrade     fn       — async (priceId) => void  (from useSubscription.startCheckout)
 *   meditationsLeft number — remaining free meditations (null = hidden)
 *   compact       bool     — smaller inline variant
 */
export default function PaywallCard({ feature, reason, onUpgrade, meditationsLeft, compact = false }) {
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    const priceId = selectedPlan === 'yearly' ? YEARLY_PRICE : MONTHLY_PRICE;
    if (!priceId) {
      toast.error('Payment not configured yet — contact support');
      return;
    }
    setLoading(true);
    try {
      await onUpgrade(priceId);
    } catch {
      toast.error('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <div style={{
        padding: '16px 20px',
        background: 'linear-gradient(135deg, rgba(30,107,104,0.08) 0%, rgba(184,211,207,0.15) 100%)',
        borderRadius: '14px',
        border: '1px solid rgba(30,107,104,0.2)',
        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <p style={{ margin: '0 0 3px', color: '#1E6B68', fontWeight: '600', fontSize: '0.9rem' }}>
            {meditationsLeft === 0
              ? 'Free limit reached'
              : `${feature} requires Pro`}
          </p>
          <p style={{ margin: 0, color: '#888', fontSize: '0.78rem' }}>
            {meditationsLeft === 0
              ? 'You\'ve used your 2 free meditations. Upgrade for unlimited.'
              : reason || 'Upgrade to Pro for full access.'}
          </p>
        </div>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            padding: '10px 22px', whiteSpace: 'nowrap',
            background: loading ? '#ccc' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff', border: 'none', borderRadius: '20px',
            fontWeight: '600', fontSize: '0.88rem', cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: '0 3px 10px rgba(30,107,104,0.25)',
          }}
        >
          {loading ? 'Loading…' : 'Upgrade — 7 days free'}
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: '40px 28px',
      background: 'linear-gradient(160deg, #0D2B2A 0%, #1E6B68 100%)',
      borderRadius: '20px', textAlign: 'center',
    }}>
      {/* Icon */}
      <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✦</div>

      <p style={{ margin: '0 0 6px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        HYPSEYA PRO
      </p>
      <h3 style={{ margin: '0 0 10px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.5rem', fontWeight: '400' }}>
        {meditationsLeft === 0 ? 'Your free meditations are complete' : `${feature} is a Pro feature`}
      </h3>
      <p style={{ margin: '0 0 28px', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', lineHeight: 1.7, maxWidth: '340px', marginLeft: 'auto', marginRight: 'auto' }}>
        {meditationsLeft === 0
          ? "You've created your 2 free meditations. To continue creating personalized practices in your own voice — upgrade to Pro."
          : reason || "This feature is part of HYPSEYA Pro — unlimited meditations, all recording sessions, and full access to your library."}
      </p>

      {/* Plan toggle */}
      <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '4px', marginBottom: '20px', gap: '4px' }}>
        {[
          { id: 'yearly',  label: '€8.25/mo', sub: 'Annual (€99/yr)' },
          { id: 'monthly', label: '€14.99/mo', sub: 'Monthly' },
        ].map(p => (
          <button
            key={p.id}
            onClick={() => setSelectedPlan(p.id)}
            style={{
              padding: '10px 18px', borderRadius: '9px', border: 'none', cursor: 'pointer',
              background: selectedPlan === p.id ? 'rgba(255,255,255,0.2)' : 'transparent',
              color: '#fff', transition: 'all 0.2s ease',
            }}
          >
            <div style={{ fontWeight: '700', fontSize: '0.88rem' }}>{p.label}</div>
            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', marginTop: '2px' }}>{p.sub}</div>
          </button>
        ))}
      </div>

      {selectedPlan === 'yearly' && (
        <div style={{ marginBottom: '16px' }}>
          <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600' }}>
            Save €80/yr vs monthly
          </span>
        </div>
      )}

      <button
        onClick={handleUpgrade}
        disabled={loading}
        style={{
          padding: '16px 40px', width: '100%', maxWidth: '320px',
          background: loading ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: '50px', color: '#fff',
          fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
          letterSpacing: '0.04em',
        }}
      >
        {loading ? 'Redirecting…' : 'Start — 7 days free'}
      </button>

      <p style={{ margin: '12px 0 0', color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
        Cancel anytime · No commitments
      </p>
    </div>
  );
}
